import { useCallback, useMemo } from 'react';
import { useCompaniesStore, companySearchableFields } from '../store';
import { useDialog } from 'components/core/Dialog/common/DialogContext';
import { useEntityCollectionInitializer } from 'store/entityCollection';
import { FormattedMessage } from 'intl';
import { translationKeyToString } from 'intl/types/translationKey';
import { DialogTranslationKeys } from 'intl/keys';

import { LabelledEntity } from 'shared/types/commonView';
import { filterBySearchPhrase } from 'components/common/SearchInput/helpers';
import { EmptyListReason } from 'components/common/noTableContentInfo/NoTableContentInfo';
import type { CompanyModel } from 'shared/domain/company/types/model';
import { CommonTranslationKeys } from 'intl/keys';

export function useCompaniesInitializer(): void {
  useEntityCollectionInitializer(useCompaniesStore.use.actions());
}

export function useCompanies(): {
  items: CompanyModel[];
  loading: boolean;
  deleteCompany: (company: LabelledEntity) => void;
  restoreCompany: (company: LabelledEntity) => void;
  searchPhrase: string;
  setSearchPhrase: (phrase: string) => void;
  emptyListReason: EmptyListReason | undefined;
} {
  const data = useCompaniesStore.use.data();
  const search = useCompaniesStore.use.search();
  const actions = useCompaniesStore.use.actions();
  const createDialog = useDialog();

  const filtered = useMemo(() => {
    return filterBySearchPhrase(
      data.items,
      search.phrase,
      companySearchableFields
    );
  }, [search.phrase, data.items]);

  const emptyListReason = useMemo(() => {
    if (data.loading) {
      return undefined;
    }
    if (data.items.length === 0) {
      if (search.phrase.length) {
        return EmptyListReason.SEARCHPHRASE_MISMATCH;
      }
      return EmptyListReason.NOTHING_IN_DB;
    }
    return undefined;
  }, [data.items.length, search.phrase.length, data.loading]);

  const deleteCompany = useCallback(
    (company: LabelledEntity) => {
      createDialog({
        title: (
          <FormattedMessage id={DialogTranslationKeys.confirmationDeleteCompanyTitle} />
        ),
        description: (
          <FormattedMessage id={DialogTranslationKeys.confirmationDeleteCompanyDescription}
            values={{ companyName: company.label }}
          />
        ),
        customControllerLabels: [
          translationKeyToString(CommonTranslationKeys.cancel),
          translationKeyToString(CommonTranslationKeys.archive),
        ],
      }).then(() => actions.archive(company._id));
    },
    [createDialog, actions]
  );

  const restoreCompany = useCallback(
    (company: LabelledEntity) => actions.restore(company._id),
    [actions]
  );

  return {
    items: filtered,
    loading: data.loading,
    deleteCompany,
    restoreCompany,
    searchPhrase: search.phrase,
    setSearchPhrase: actions.setSearchPhrase,
    emptyListReason,
  };
}

/**
 * Returns all companies from the store without search-phrase filtering.
 * Callers must apply `keepUndeleted` (or equivalent) before using items in forms.
 */
export function useAllCompanies(): {
  items: CompanyModel[];
  loading: boolean;
} {
  const data = useCompaniesStore.use.data();

  return useMemo(
    () => ({
      items: data.items,
      loading: data.loading,
    }),
    [data.items, data.loading]
  );
}
